import React, { useEffect, useState } from 'react';
// Global styles have been migrated to MUI GlobalStyles and theme; index.css removed
import { 
  ThemeProvider, 
  createTheme, 
  CssBaseline, 
  Typography, 
  Box,
  GlobalStyles
} from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import { posthog } from './analytics/posthog';
import { AuthProvider } from './components/auth';
import MainApp from './components/MainApp';

// Design tokens for light/dark modes
const getDesignTokens = (mode: 'light' | 'dark') => ({
  palette: {
    mode,
    primary: {
      light: '#A8E6A3',
      main: '#4CAF50',
      dark: '#2E7D32',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#30ABF0',
      light: '#5CC0F5',
      dark: '#1C8BD1',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#ffffff',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#ffffff',
    },
    info: {
      main: '#06b6d4',
      light: '#22d3ee',
      dark: '#0891b2',
      contrastText: '#ffffff',
    },
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
    background: mode === 'dark'
      ? { default: '#0f172a', paper: 'rgba(0,0,0,0.35)', strongPaper: '#1f2937' as any }
      : { default: '#FFFFFF', paper: 'rgba(255,255,255,0.3)', strongPaper: '#ffffff' as any },
    text: mode === 'dark' ? { primary: '#e5e7eb', secondary: '#cbd5e1' } : { primary: '#212121', secondary: '#424242' },
    divider: mode === 'dark' ? '#334155' : '#e2e8f0',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 600 },
    h2: { fontSize: '2rem', fontWeight: 500 },
    h3: { fontSize: '1.75rem', fontWeight: 500 },
    h4: { fontSize: '1.5rem', fontWeight: 500 },
    h5: { fontSize: '1.25rem', fontWeight: 500 },
    h6: { fontSize: '1rem', fontWeight: 500 },
    body1: { fontSize: '1rem', lineHeight: 1.5 },
    body2: { fontSize: '0.875rem', lineHeight: 1.43 },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '8px 16px',
        } as any,
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          },
        } as any,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 12,
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          // Central translucent surface for all Paper components
          // Note: component-level sx props will still override this when needed
          backgroundColor: 'rgba(255,255,255,0.3)',
          color: 'inherit',
          border: '1px solid',
          borderColor: 'divider',
        } as any,
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
      defaultProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              bgcolor: (t: any) => (t.palette.background as any).strongPaper,
            },
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        PaperProps: {
          sx: {
            bgcolor: (t: any) => (t.palette.background as any).strongPaper,
          },
        },
      },
      styleOverrides: {
        paper: ({ theme }: any) => ({
          backgroundColor: (theme.palette.background as any).strongPaper,
        }),
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          backgroundColor: (theme.palette.background as any).strongPaper,
        }),
      },
    },
    MuiDialog: {
      defaultProps: {
        PaperProps: {
          sx: {
            bgcolor: (t: any) => (t.palette.background as any).strongPaper,
          },
        },
      },
      styleOverrides: {
        paper: ({ theme }: any) => ({
          backgroundColor: (theme.palette.background as any).strongPaper,
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }: any) => ({
          backgroundColor: (theme.palette.background as any).strongPaper,
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
          },
        }),
      },
    },
    // Centralized Accordion styling (borderless cards with subtle separators)
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          border: 'none',
          boxShadow: 'none',
          // Use translucent paper so the background gradient shows through
          backgroundColor: (theme.palette.background as any).paper,
          backdropFilter: 'saturate(160%) blur(6px)',
          // Center and slightly narrow relative to the parent Paper on wide screens
          margin: `${theme.spacing(1)} auto`,
          width: '100%',
          maxWidth: 880,
          [theme.breakpoints.down('md')]: {
            maxWidth: '100%',
          },
          borderRadius: theme.shape.borderRadius,
          overflow: 'hidden',
          // Keep centered width when expanded (MUI sets margin: 16px 0 on .Mui-expanded)
          '&.Mui-expanded': {
            margin: `${theme.spacing(1)} auto`,
            maxWidth: 880,
            [theme.breakpoints.down('md')]: {
              maxWidth: '100%',
            },
          },
          '&:before': {
            // remove default MUI top divider
            display: 'none',
          },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          minHeight: 56,
          // Keep transparent summary to preserve translucent look
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${theme.palette.divider}`,
          '& .MuiAccordionSummary-content': {
            margin: `${theme.spacing(1)} 0`,
          },
        }),
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          padding: theme.spacing(2, 2.5),
          backgroundColor: 'transparent',
          '& .MuiTypography-root': {
            width: '100%',
            maxWidth: 880,
            marginLeft: 'auto',
            marginRight: 'auto',
            [theme.breakpoints.down('md')]: {
              maxWidth: '100%',
            },
          },
        }),
      },
    },
  },
});

// Main App Component
const App: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  // Force light mode regardless of system settings
  const theme = createTheme(getDesignTokens('light'));

  useEffect(() => {
    // Initialize PostHog
    if (posthog) {
      posthog.init(process.env.REACT_APP_POSTHOG_KEY || '', {
        api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com'
      });
    }

    // Mark as initialized
    setIsInitialized(true);
  }, []);

  if (!isInitialized) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
        styles={{
            '*': { boxSizing: 'border-box' },
            body: {
              margin: 0,
              padding: 0,
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
            },
            '#root': { minHeight: '100vh', display: 'flex', flexDirection: 'column' },
            '.MuiContainer-root': { paddingLeft: '16px', paddingRight: '16px' },
            '@media (min-width: 600px)': {
              '.MuiContainer-root': { paddingLeft: '24px', paddingRight: '24px' },
            },
            '::-webkit-scrollbar': { width: '8px', height: '8px' },
            '::-webkit-scrollbar-track': { background: theme.palette.divider, borderRadius: '4px' },
            '::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'dark' ? '#475569' : '#c1c1c1',
              borderRadius: '4px',
              '&:hover': { background: theme.palette.mode === 'dark' ? '#64748b' : '#a8a8a8' },
            },
          // Long-form content: left-align on small screens, justify on md+
          '.content-text .MuiTypography-body1, .content-text .MuiTypography-body2': {
            textAlign: 'left',
            textJustify: 'inter-word',
            hyphens: 'auto',
            [theme.breakpoints.up('md')]: {
              textAlign: 'justify',
            },
          },
          }}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="background.default"
        >
          <Typography variant="h6" color="text.secondary">
            Loading Productive Capabilities Data Lab...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            '*': { boxSizing: 'border-box' },
            body: {
              margin: 0,
              padding: 0,
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
            },
            '#root': { minHeight: '100vh', display: 'flex', flexDirection: 'column' },
            '.MuiContainer-root': { paddingLeft: '16px', paddingRight: '16px' },
            '@media (min-width: 600px)': {
              '.MuiContainer-root': { paddingLeft: '24px', paddingRight: '24px' },
            },
            '::-webkit-scrollbar': { width: '8px', height: '8px' },
            '::-webkit-scrollbar-track': { background: theme.palette.divider, borderRadius: '4px' },
            '::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'dark' ? '#475569' : '#c1c1c1',
              borderRadius: '4px',
              '&:hover': { background: theme.palette.mode === 'dark' ? '#64748b' : '#a8a8a8' },
            },
            // Long-form content: left-align on small screens, justify on md+
            '.content-text .MuiTypography-body1, .content-text .MuiTypography-body2': {
              textAlign: 'left',
              textJustify: 'inter-word',
              hyphens: 'auto',
              [theme.breakpoints.up('md')]: {
                textAlign: 'justify',
              },
            },
          }}
        />
        <AuthProvider>
          <MainApp />
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
